import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '../api/client';

// Mirrors backend ControlledVocabRow / ExternalOrgRow.
export interface ControlledVocabRow {
  id: number;
  vocab_key: string;
  value: string;
  label: string;
  description_terse: string | null;
  description_human: string | null;
  prompt_examples: string | null;
  predicate_text: string | null;
  display_order: number;
  active: boolean;
  origin: string;
  vocab_version_id: number;
  attrs: Record<string, string>;
}

export interface ControlledVocabListResponse {
  rows: ControlledVocabRow[];
  by_vocab_key: Record<string, number>;
}

export interface ExternalOrgRow {
  id: number;
  canonical_name: string;
  display_name: string | null;
  industry: string | null;
  hq_location: string | null;
  brief_description: string | null;
  status: string;
  origin: string;
  domain_count: number;
  assignment_count: number;
}

export interface ExternalOrgListResponse {
  rows: ExternalOrgRow[];
  total: number;
}

export interface ExternalOrgDomainRow {
  domain: string;
  is_primary: boolean;
  origin: string;
}

export interface ExternalOrgAssignmentRow {
  vocab_key: string;
  value: string;
  confidence: string | null;
  evidence: string | null;
  origin: string;
  assigned_at: string;
}

export interface ExternalOrgDetail {
  org: ExternalOrgRow;
  domains: ExternalOrgDomainRow[];
  assignments: ExternalOrgAssignmentRow[];
}

export function useControlledVocabList(vocabKey: string | null, includeInactive = false) {
  const params = new URLSearchParams();
  if (vocabKey) params.set('vocab_key', vocabKey);
  if (includeInactive) params.set('include_inactive', 'true');
  const qs = params.toString();
  return useQuery<ControlledVocabListResponse>({
    queryKey: ['vocab', 'list', vocabKey ?? 'all', includeInactive],
    queryFn: () => apiFetch<ControlledVocabListResponse>(
      `/api/admin/vocabularies${qs ? `?${qs}` : ''}`,
    ),
    staleTime: 60_000,
  });
}

export function useExternalOrgList(q: string, status: string | null = null) {
  const params = new URLSearchParams();
  if (q) params.set('q', q);
  if (status) params.set('status', status);
  params.set('limit', '50');
  return useQuery<ExternalOrgListResponse>({
    queryKey: ['vocab', 'external-orgs', q, status],
    queryFn: () => apiFetch<ExternalOrgListResponse>(
      `/api/admin/external-orgs?${params.toString()}`,
    ),
    staleTime: 60_000,
  });
}

export function useExternalOrgDetail(orgId: number | null) {
  return useQuery<ExternalOrgDetail>({
    queryKey: ['vocab', 'external-org', orgId],
    queryFn: () => apiFetch<ExternalOrgDetail>(
      `/api/admin/external-orgs/${orgId}`,
    ),
    enabled: orgId != null,
    staleTime: 60_000,
  });
}


// ---------------------------------------------------------------------------
// A1 — vocab edit mutations + audit + violation triage
// ---------------------------------------------------------------------------

export interface VocabCreatePayload {
  vocab_key: string;
  value: string;
  label?: string;
  description_terse?: string;
  description_human?: string;
  prompt_examples?: string;
  predicate_text?: string;
  display_order?: number;
  attrs?: Record<string, string>;
}

export interface VocabUpdatePayload {
  label?: string;
  description_terse?: string;
  description_human?: string;
  prompt_examples?: string;
  predicate_text?: string;
  display_order?: number;
  active?: boolean;
  // null value clears the attr; string value sets it.
  attrs?: Record<string, string | null>;
}

export interface VocabAuditRow {
  id: number;
  vocab_id: number;
  vocab_key: string;
  value: string;
  action: string;
  changes_json: string | null;
  attrs_changes_json: string | null;
  edited_by_email: string | null;
  edited_at: string;
}

export interface VocabAuditResponse {
  rows: VocabAuditRow[];
}

export interface VocabGapNearest {
  value: string;
  why_not: string | null;
}

/**
 * One worklist row — a membership rejection (`kind: 'value'`) or a vocabulary gap (`kind: 'gap'`).
 *
 * There is deliberately NO `phrase` and NO evidence-ref field: `vocab_violation_log` carries no
 * `permission_class` and this page is firm-wide, so the grounding sentence travels only through the
 * governed evidence reader under the subject's own audience (primitive spec §5, "no body content,
 * ever"). `description` and `nearest[].why_not` are machine-written definitions, scrubbed of emails
 * and URLs server-side — an `[email]` / `[url]` marker means text was redacted, not that the model
 * wrote it that way.
 */
export interface VocabViolationRow {
  id: number;
  kind: string;
  vocab_key: string;
  raw_value: string;
  count: number;
  /** DISTINCT subjects — what the per-key surface threshold counts (D-P4), not hits and not deals. */
  subject_count: number;
  surfaced: boolean;
  first_seen_at: string;
  last_seen_at: string;
  status: string;
  proposed_label: string | null;
  description: string | null;
  nearest: VocabGapNearest[];
  /** The LATEST subject and producer, rendered without a join. The full list is the backfill's. */
  subject_kind: string | null;
  subject_ref: string | null;
  producer_kind: string | null;
  record_ref: string | null;
  backfill_subjects: number | null;
  backfill_filled: number | null;
  backfill_refused_human: number | null;
  backfill_still_unmapped: number | null;
  /** When the backfill last found this promoted row unservable — every remaining subject belongs to
   *  a producer with no registered executor. `null` for every other row. */
  backfill_awaiting_executor_at: string | null;
  example_entity_kind: string | null;
  example_entity_key: string | null;
}

export interface VocabViolationListResponse {
  rows: VocabViolationRow[];
}

export function useCreateVocabRow() {
  const qc = useQueryClient();
  return useMutation<ControlledVocabRow, Error, VocabCreatePayload>({
    mutationFn: (payload) =>
      apiFetch<ControlledVocabRow>('/api/admin/vocabularies', {
        method: 'POST',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vocab', 'list'] });
      qc.invalidateQueries({ queryKey: ['vocab', 'audit'] });
      // Phase 2 — bust the Deals card dropdown cache so admin edits
      // propagate immediately to the FieldDropdown on the Deals tab.
      qc.invalidateQueries({ queryKey: ['deals', 'field-options'] });
    },
  });
}

export function useUpdateVocabRow() {
  const qc = useQueryClient();
  return useMutation<
    ControlledVocabRow,
    Error,
    { vocabId: number; payload: VocabUpdatePayload }
  >({
    mutationFn: ({ vocabId, payload }) =>
      apiFetch<ControlledVocabRow>(`/api/admin/vocabularies/${vocabId}`, {
        method: 'PATCH',
        body: JSON.stringify(payload),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vocab', 'list'] });
      qc.invalidateQueries({ queryKey: ['vocab', 'audit'] });
      qc.invalidateQueries({ queryKey: ['deals', 'field-options'] });
    },
  });
}

export function useVocabAudit(vocabKey: string | null = null) {
  const params = new URLSearchParams();
  if (vocabKey) params.set('vocab_key', vocabKey);
  const qs = params.toString();
  return useQuery<VocabAuditResponse>({
    queryKey: ['vocab', 'audit', vocabKey ?? 'all'],
    queryFn: () =>
      apiFetch<VocabAuditResponse>(`/api/admin/vocab-audit${qs ? `?${qs}` : ''}`),
    staleTime: 30_000,
  });
}

export function useVocabViolations(
  status: string = 'open',
  vocabKey: string | null = null,
) {
  const params = new URLSearchParams();
  params.set('status', status);
  if (vocabKey) params.set('vocab_key', vocabKey);
  return useQuery<VocabViolationListResponse>({
    queryKey: ['vocab', 'violations', status, vocabKey ?? 'all'],
    queryFn: () =>
      apiFetch<VocabViolationListResponse>(
        `/api/admin/vocab-violations?${params.toString()}`,
      ),
    staleTime: 30_000,
  });
}

export function useAddViolationToVocab() {
  const qc = useQueryClient();
  return useMutation<
    ControlledVocabRow,
    Error,
    {
      violationId: number;
      label?: string;
      description_terse?: string;
      description_human?: string;
    }
  >({
    mutationFn: ({ violationId, ...body }) =>
      apiFetch<ControlledVocabRow>(
        `/api/admin/vocab-violations/${violationId}/add-to-vocab`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vocab', 'list'] });
      qc.invalidateQueries({ queryKey: ['vocab', 'violations'] });
      qc.invalidateQueries({ queryKey: ['vocab', 'audit'] });
      qc.invalidateQueries({ queryKey: ['deals', 'field-options'] });
    },
  });
}

export interface PromoteAsNewBody {
  label: string;
  description_terse: string;
  domain: string;
  materiality_rank: number;
  required_qualifiers?: string[];
  description_human?: string;
}

/**
 * Promote a gap into a real register value. Every attr a register row NEEDS is required, because the
 * legacy one-click route is what shipped values with none (primitive spec §6). A contract-bearing key
 * (`relationship_type`) is refused with 409 unless the E0-AB contract is in place.
 */
export function usePromoteGapAsNew() {
  const qc = useQueryClient();
  return useMutation<ControlledVocabRow, Error, { violationId: number } & PromoteAsNewBody>({
    mutationFn: ({ violationId, ...body }) =>
      apiFetch<ControlledVocabRow>(
        `/api/admin/vocab-violations/${violationId}/promote-as-new`,
        { method: 'POST', body: JSON.stringify(body) },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vocab', 'list'] });
      qc.invalidateQueries({ queryKey: ['vocab', 'violations'] });
      qc.invalidateQueries({ queryKey: ['vocab', 'audit'] });
      qc.invalidateQueries({ queryKey: ['deals', 'field-options'] });
    },
  });
}

export interface VocabDeclineResponse {
  id: number;
  status: string;
  vocab_key: string;
  raw_value: string;
}

/** Decline a gap: worklist only, sticky, and it writes no register row — not even a retired one. */
export function useDeclineGap() {
  const qc = useQueryClient();
  return useMutation<VocabDeclineResponse, Error, { violationId: number }>({
    mutationFn: ({ violationId }) =>
      apiFetch<VocabDeclineResponse>(
        `/api/admin/vocab-violations/${violationId}/decline`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vocab', 'violations'] });
    },
  });
}

export function useDismissViolation() {
  const qc = useQueryClient();
  return useMutation<VocabViolationRow, Error, { violationId: number }>({
    mutationFn: ({ violationId }) =>
      apiFetch<VocabViolationRow>(
        `/api/admin/vocab-violations/${violationId}/dismiss`,
        { method: 'POST' },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['vocab', 'violations'] });
    },
  });
}
